import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
  getSuppliers, 
  getSupplier,
  createSupplier, 
  updateSupplier, 
  deleteSupplier,
  exportSuppliers,
  importSuppliers
} from '../../../models/suppliersModel';
import { useHasAnyPermission } from '../../../utils/permissions';
import AddSupplierModal from './modals/AddSupplierModal';
import EditSupplierModal from './modals/EditSupplierModal';
import ViewSupplierModal from './modals/ViewSupplierModal';
import ImportSuppliersModal from './modals/ImportSuppliersModal';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

// Debounced Input for Global Filter
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (value !== initialValue) {
        onChange(value);
      }
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, initialValue, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

function Suppliers() {
  // Permission checks - temporarily set to true for debugging
  const canViewSuppliers = true; // useHasAnyPermission(['View Suppliers']);
  const canAddSuppliers = true; // useHasAnyPermission(['Add Suppliers']);
  const canEditSuppliers = true; // useHasAnyPermission(['Edit Suppliers']);
  const canDeleteSuppliers = true; // useHasAnyPermission(['Delete Suppliers']);
  const canExportSuppliers = true; // useHasAnyPermission(['Export Suppliers']);
  const canImportSuppliers = true; // useHasAnyPermission(['Import Suppliers']);

  const [suppliers, setSuppliers] = useState([]);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load initial pagination state from localStorage, or use defaults
  const initialPagination = () => {
    const storedPagination = localStorage.getItem('suppliersTablePagination');
    if (storedPagination) {
      return JSON.parse(storedPagination);
    }
    return {
      pageIndex: 0,
      pageSize: 10,
    };
  };

  // Server-side state
  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [viewSupplierLoading, setViewSupplierLoading] = useState(false);
  const [viewSupplierError, setViewSupplierError] = useState(null);

  // Save pagination state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('suppliersTablePagination', JSON.stringify(pagination));
  }, [pagination]);

  // Memoize columns
  const columns = useMemo(() => [
    {
      accessorKey: 'BP_Code',
      header: 'BP Code',
      cell: info => <span className="font-medium text-gray-800">{info.getValue()}</span>,
    },
    {
      accessorKey: 'BP_Name',
      header: 'Company Name',
      cell: info => <span className="text-gray-700">{info.getValue()}</span>,
    },
    {
      accessorKey: 'Group_Code',
      header: 'Services',
      cell: info => {
        const groupCode = info.getValue();
        if (!groupCode || groupCode.trim() === '') {
          return <span className="text-gray-400 text-xs">None specified</span>;
        }
        
        // Group_Code is stored as comma-separated service codes
        const serviceCodes = groupCode.split(',').map(s => s.trim()).filter(Boolean);
        
        if (serviceCodes.length === 0) {
          return <span className="text-gray-400 text-xs">None specified</span>;
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {serviceCodes.slice(0, 2).map((code, idx) => (
              <span 
                key={idx} 
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                title={code}
              >
                {code}
              </span>
            ))}
            {serviceCodes.length > 2 && (
              <span 
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                title={serviceCodes.slice(2).join(', ')}
              >
                +{serviceCodes.length - 2} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'Contact_Person',
      header: 'Contact Person',
      cell: info => <span className="text-gray-700">{info.getValue() || 'N/A'}</span>,
    },
    {
      accessorKey: 'E_Mail',
      header: 'Email',
      cell: info => info.getValue() ? (
        <a href={`mailto:${info.getValue()}`} className="text-blue-600 hover:text-blue-800">
          {info.getValue()}
        </a>
      ) : (
        <span className="text-gray-700">N/A</span>
      ),
    },
    {
      accessorKey: 'Status',
      header: 'Status',
      cell: info => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          info.getValue() == 1 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {info.getValue() == 1 ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-1">
          <button
            type="button"
            className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
            onClick={() => handleView(row.original)}
            title="View Supplier"
          >
            View
          </button>
          {canEditSuppliers && (
            <button
              type="button"
              className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs"
              onClick={() => handleEdit(row.original)}
              title="Edit Supplier"
            >
              Edit
            </button>
          )}
          {canDeleteSuppliers && (
            <button
              type="button"
              className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
              onClick={() => handleDelete(row.original)}
              title="Delete Supplier"
            >
              Delete
            </button>
          )}
        </div>
      ),
      enableSorting: false,
    },
  ], [canEditSuppliers, canDeleteSuppliers]);

  const fetchData = useCallback(async () => {
    console.log('fetchData called with pagination:', pagination, 'globalFilter:', globalFilter);
    setLoading(true);
    try {
      // Convert react-table pagination to API format
      const page = pagination.pageIndex + 1; // API is 1-based
      const perPage = pagination.pageSize;
      
      console.log('Calling getSuppliers with page:', page, 'perPage:', perPage, 'search:', globalFilter);
      const response = await getSuppliers(page, perPage, globalFilter, 1); // Only show active suppliers (Status = 1)
      console.log('getSuppliers response:', response);

      if (response.status === 'success') {
        console.log('Setting suppliers:', response.data);
        setSuppliers(response.data || []);
        setRowCount(response.total || 0);
        // Set supplier groups from the same response
        if (response.groups) {
          setSupplierGroups(response.groups);
        }
        console.log('Set rowCount to:', response.total);
      } else {
        throw new Error(response.message || 'Failed to load suppliers');
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers: ' + err.message);
      toast.error('Failed to load suppliers');
      setSuppliers([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter]); // Dependencies for useCallback

  useEffect(() => {
    fetchData();
  }, [fetchData]); // fetchData is now stable due to useCallback

  const table = useReactTable({
    data: suppliers,
    columns,
    state: {
      pagination,
      globalFilter,
      sorting,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    debugTable: false,
  });

  const handleView = async (supplier) => {
    setSelectedSupplier({ ...supplier, users: undefined });
    setViewSupplierError(null);
    setShowViewModal(true);
    setViewSupplierLoading(true);

    try {
      const response = await getSupplier(supplier.BP_Code);

      if (response.status === 'success') {
        setSelectedSupplier(prev => ({
          ...(prev || {}),
          ...(response.data || {})
        }));
      } else {
        const message = response.message || 'Failed to load supplier details';
        setViewSupplierError(message);
        toast.error(message);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load supplier details';
      setViewSupplierError(message);
      toast.error(message);
    } finally {
      setViewSupplierLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedSupplier(null);
    setViewSupplierError(null);
    setViewSupplierLoading(false);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setShowEditModal(true);
  };

  const handleDelete = async (supplier) => {
    if (window.confirm(`Are you sure you want to deactivate supplier "${supplier.BP_Name}"?`)) {
      try {
        const response = await deleteSupplier(supplier.BP_Code);
        if (response.status === 'success') {
          toast.success('Supplier deactivated successfully!');
          fetchData(); // Refresh the data
        } else {
          throw new Error(response.message || 'Failed to deactivate supplier');
        }
      } catch (error) {
        console.error('Error deactivating supplier:', error);
        toast.error('Failed to deactivate supplier');
      }
    }
  };

  const handleAdd = () => {
    setShowAddModal(true);
  };

  const handleAddSubmit = async (formData) => {
    try {
      const response = await createSupplier(formData);
      if (response.status === 'success') {
        toast.success('Supplier added successfully!');
        setShowAddModal(false);
        fetchData(); // Refresh the data
      } else {
        throw new Error(response.message || 'Failed to add supplier');
      }
    } catch (error) {
      console.error('Error adding supplier:', error);
      toast.error('Failed to add supplier');
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const response = await updateSupplier(selectedSupplier.BP_Code, formData);
      if (response.status === 'success') {
        toast.success('Supplier updated successfully!');
        setShowEditModal(false);
        fetchData(); // Refresh the data
      } else {
        throw new Error(response.message || 'Failed to update supplier');
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      toast.error('Failed to update supplier');
    }
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleImportSubmit = async (file) => {
    try {
      const response = await importSuppliers(file);
      if (response.status === 'success') {
        toast.success('Suppliers imported successfully!');
        setShowImportModal(false);
        fetchData(); // Refresh the data
      } else {
        throw new Error(response.message || 'Failed to import suppliers');
      }
    } catch (error) {
      console.error('Error importing suppliers:', error);
      toast.error('Failed to import suppliers');
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportSuppliers();
      if (response.status === 'success') {
        // Create and trigger download
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Suppliers_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Suppliers exported successfully!');
      } else {
        throw new Error(response.message || 'Failed to export suppliers');
      }
    } catch (error) {
      console.error('Error exporting suppliers:', error);
      toast.error('Failed to export suppliers');
    }
  };

  // Render loading state separately for clarity
  if (loading && suppliers.length === 0) { // Show full page loader only on initial load
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Professional Header Card */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-2">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9m0 0h6m-6 0V5a2 2 0 012-2h4a2 2 0 012 2v4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Suppliers Management</h1>
                <p className="text-xs text-gray-500 mb-1">Business Partners & Vendor Management</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                    {globalFilter && (
                      <span className="ml-1 text-blue-600">(filtered)</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Page Size Filter */}
                <div className="flex-shrink-0">
                  <select
                    value={pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 20, 30, 50].map(size => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </select>
                </div>

                {/* Import Button */}
                {canImportSuppliers && (
                  <button
                    onClick={handleImport}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    Import
                  </button>
                )}

                {/* Export Button */}
                {canExportSuppliers && (
                  <button
                    onClick={handleExport}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export
                  </button>
                )}

                {/* Add Button */}
                {canAddSuppliers && (
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                    style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0087D4' }}
                  >
                    <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Supplier
                  </button>
                )}
              </div>
              
              {/* Row 2: Search Box */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={globalFilter ?? ''}
                  onChange={e => {
                    setGlobalFilter(String(e.target.value));
                    setPagination(p => ({ ...p, pageIndex: 0 }));
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search suppliers..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4">

        {/* Data Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      scope="col"
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                      style={{ color: '#0087D4' }}
                      onClick={header.column.getToggleSortingHandler()}
                      title={header.column.getCanSort() ? (header.column.getNextSortingOrder() === 'asc' ? 'Sort ascending' : header.column.getNextSortingOrder() === 'desc' ? 'Sort descending' : 'Clear sort') : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <span className="ml-1 text-blue-600">↑</span>,
                        desc: <span className="ml-1 text-blue-600">↓</span>,
                      }[header.column.getIsSorted()] ?? null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && suppliers.length > 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-6 text-center">
                    <p className="text-gray-600">
                      {globalFilter ? "No suppliers found matching your search." : "No suppliers found"}
                    </p>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-blue-50 transition-colors">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-3 py-2 text-sm text-gray-700">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Enhanced Pagination */}
        {rowCount > 0 && (
          <div className="px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" style={{ backgroundColor: 'rgba(0, 135, 212, 0.02)' }}>
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((pagination.pageIndex) * pagination.pageSize) + 1} to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount)} of {rowCount} results
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
              >
                First
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const totalPages = Math.ceil(rowCount / pagination.pageSize);
                  const currentPage = pagination.pageIndex + 1;
                  const pageNumbers = [];
                  
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => table.setPageIndex(i - 1)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium transition-colors ${
                          i === currentPage
                            ? 'text-white border-transparent'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                        style={i === currentPage ? { backgroundColor: '#0087D4', borderColor: '#0087D4' } : {}}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
              >
                Next
              </button>
              <button
                onClick={() => table.setPageIndex(Math.ceil(rowCount / pagination.pageSize) - 1)}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddSupplierModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddSubmit}
          groups={supplierGroups}
        />
      )}

      {showEditModal && (
        <EditSupplierModal
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSubmit}
          supplier={selectedSupplier}
          groups={supplierGroups}
        />
      )}

      {showViewModal && selectedSupplier && (
        <ViewSupplierModal
          onClose={handleCloseViewModal}
          supplier={selectedSupplier}
          loading={viewSupplierLoading}
          error={viewSupplierError}
          onRefresh={() => handleView(selectedSupplier)}
        />
      )}

      {showImportModal && (
        <ImportSuppliersModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSubmit}
        />
      )}
      </div>
    </div>
  );
}

export default Suppliers;
